/* eslint-disable complexity */
/* eslint-disable no-case-declarations */
import type { Styles } from '@adornis/ass/style';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { RXController } from '@adornis/chemistry/controllers/RXController';
import { css } from '@adornis/chemistry/directives/css';
import { signalToObservable } from '@adornis/functional-lit/signal-to-observable';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, startWith, Subject } from 'rxjs';
import { DubniumDialog } from '../../client/theme/d-dialog';
import { CookieFactory } from './types/CookieFactory';
import type { IConsentConfig, IConsentGroup } from './types/IConsentConfig';

type ConsentState = Record<string, boolean>;
export type ConsentEvent = 'track-page';

function flatGroupConfigs(groups: Array<IConsentGroup | IConsentConfig>): IConsentConfig[] {
  return groups
    .map(data => {
      if (isIConsentConfig(data)) return [data];
      return data.items;
    })
    .flat();
}

function isIConsentConfig(data: IConsentGroup | IConsentConfig): data is IConsentConfig {
  if ('items' in data) return false;
  return true;
}

function convertGroupsToState(groups: Array<IConsentGroup | IConsentConfig>) {
  const configs = flatGroupConfigs(groups);
  const state: ConsentState = {};
  for (const config of configs) {
    const key = config.key;
    const localStorageValue = CookieFactory.consentCookie.getValue(key) ?? 'false';
    const value = !!JSON.parse(localStorageValue);
    state[key] = value;
  }
  return state;
}

export const consentCookieValueSubject = new Subject<string | undefined>();

@customElement('consent-tool')
export class ConsentTool extends ChemistryLitElement {
  @property({ attribute: false }) groups = new RXController<Maybe<Array<IConsentGroup | IConsentConfig>>>(
    this,
    undefined,
  );
  @state() private _openPopup: boolean = false;

  private _interval?: NodeJS.Timeout;

  override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    consentCookieValueSubject.next(CookieFactory.consentCookie.getCookie());

    // startWith(null) to trigger it initial
    consentCookieValueSubject.pipe(startWith(null)).subscribe(value => {
      const isInitialized = CookieFactory.consentCookie.getValue('init_consent', 'boolean');
      if (isInitialized) return;
      const groups = this.groups.value;
      this._openPopup = false;
      return ConsentInitialDialog.showPopup<ConsentState>({
        modal: true,
        closeOnEscape: false,
        closeOnNavigate: false,
        props: { groups },
      }).then((newState: ConsentState) => {
        CookieFactory.consentCookie.setValue('init_consent', 'true');
        this._setState(newState);
      });
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    // avoid memory leaks
    if (this._interval) clearInterval(this._interval);
  }

  override connectedCallback(): void {
    super.connectedCallback();

    combineLatest([
      this.groups.observable.pipe(filter(Boolean)),
      signalToObservable(routingStateSignal).pipe(filter(Boolean)),
    ])
      .pipe(
        distinctUntilChanged(([prevGroups, prevRoutingState], [currGroups, currRoutingState]) => {
          return prevGroups.length === currGroups.length && prevRoutingState.path === currRoutingState.path;
        }),
        debounceTime(100), // TODO: das wird gebraucht, weil sonst im subscribe window.location.pathname noch der alte Pfad ist. Reihenfolge mus in Engine gefixt werden.
      )
      .subscribe(([groups, routingState]) => {
        const configs = flatGroupConfigs(groups);
        for (const config of configs) {
          const action = config.action;
          if (!action) continue;
          const isActive = CookieFactory.consentCookie.getValue(config.key, 'boolean');
          action(isActive === undefined ? false : isActive, 'track-page');
        }
      });
  }

  override render() {
    if (document.location.pathname.startsWith('/admin')) return nothing;

    const groups = this.groups.value;
    if (!groups || !CookieFactory.consentCookie.getValue('init_consent', 'boolean')) return nothing;

    if (this._openPopup) {
      return html`
        <consent-popup
          .groups=${groups}
          @state-changed=${(e: ValueEvent<ConsentState>) => {
            const newState = e.detail.value;
            this._setState(newState);
            this._openPopup = false;
          }}
        ></consent-popup>
      `;
    }

    return html`
      <d-flex
        ${css({
          padding: this.spacing.sm,
          background: this.colors.accent,
          borderRadius: '50%',
          color: '#333',
          fontSize: '24px',
          cursor: 'pointer',
          userSelect: 'none',
        })}
        @click=${() => {
          this._openPopup = true;
        }}
      >
        <d-icon solid> cookie-bite </d-icon>
        <d-tooltip> Cookie Einstellungen </d-tooltip>
      </d-flex>
    `;
  }

  private _setState(state: ConsentState) {
    const cookie = CookieFactory.consentCookie;
    for (const [key, value] of Object.entries(state)) {
      cookie.setValue(key, String(value));
      const action = flatGroupConfigs(this.groups.value ?? []).find(config => config.key === key)?.action;
      if (!action) continue;
      action(value);
    }
    consentCookieValueSubject.next(CookieFactory.consentCookie.getCookie());
    this.requestUpdate();
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: '99999',
          maxHeight: '90%',
          maxWidth: '100%',
          overflow: 'auto',
        },
      },
    ] as Styles[];
  }
}

@customElement('consent-popup')
export class ConsentPopup extends ChemistryLitElement {
  @property({ attribute: false }) groups = new RXController<Array<IConsentGroup | IConsentConfig>>(this, undefined);
  @state() private readonly _state = new RXController(
    this,
    this.groups.observable.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      map(groups => {
        return convertGroupsToState(groups);
      }),
    ),
  );

  override render() {
    const groups = this.groups.value;
    const state = this._state.value;
    if (!groups || !groups.length || !state) return html` invalid data `;

    return html`
      <d-flex
        ${css({
          borderRadius: '4px',
          background: '#333',
          color: '#fff',
          padding: '16px',
          gap: '32px',
        })}
      >
        <d-text>
          Wir setzen auf unseren Websites Cookies und andere Technologien ein. Einige von ihnen sind essenziell (z.B.
          für die Sicherheit der Websites) andere sind optional und helfen uns dabei, die Nutzung unserer Websites für
          Dich zu verbessern. Details zu verwendeten Cookies und Technologien findest Du in unserer
          <d-link href="https://digitale-helden.de/startseite/datenschutz/" color="#fff"> Datenschutzerklärung </d-link
          >.
        </d-text>
        <!-- <d-hr></d-hr> -->
        <consent-item-groups .groups=${groups} .state=${state}></consent-item-groups>
        <!-- <d-hr></d-hr> -->
        <d-flex horizontal crossaxis-center end space="sm" wrap>
          <d-button
            tone="black"
            small-padding
            @click=${() => {
              this.dispatchEvent(new CustomEvent('state-changed', { detail: { value: state } }));
            }}
          >
            ${'Auswahl speichern'}
          </d-button>
          <d-button
            small-padding
            @click=${() => {
              for (const key of Object.keys(state)) {
                state[key] = true;
              }
              this.dispatchEvent(new CustomEvent('state-changed', { detail: { value: state } }));
            }}
          >
            Alle akzeptieren
          </d-button>
        </d-flex>
      </d-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'block',
          width: 'min(500px, 90%)',
          position: 'relative',
          marginLeft: 'auto',
        },
      },
    ] as Styles[];
  }
}

@customElement('consent-item-groups')
export class ConsentItemGroups extends ChemistryLitElement {
  @property({ attribute: false }) groups?: Array<IConsentGroup | IConsentConfig>;
  @property({ attribute: false }) state?: ConsentState;

  @state() private _openGroups: string[] = [];

  override render() {
    const groups = this.groups;
    const state = this.state;
    if (!groups || !groups.length || !state) return nothing;

    return html`
      <x-flex space="md">
        ${repeat(
          groups,
          group => group,
          data => {
            if (isIConsentConfig(data)) return this._renderConfig(data, state);
            return this._renderGroup({ group: data, state });
          },
        )}
      </x-flex>
    `;
  }

  private _renderGroup({ group, state }: { group: IConsentGroup; state: ConsentState }) {
    const groupConfigs = group.items;
    const isEveryItemActive = groupConfigs.every(c => state[c.key]);
    const isOpen = this._openGroups.includes(group.key);

    return html`
      <d-flex space="md">
        <d-flex horizontal crossaxis-center space="sm">
          <d-checkbox
            .value=${isEveryItemActive}
            @value-picked=${(e: ValueEvent<boolean>) => {
              for (const config of groupConfigs) {
                const key = config.key;
                state[key] = e.detail.value;
              }
              this.requestUpdate();
            }}
          ></d-checkbox>
          <d-flex space="sm">
            <d-text> ${group.title} </d-text>
            <d-flex
              horizontal
              crossaxis-center
              space="xxs"
              ${css({ color: this.colors.accent, cursor: 'pointer', userSelect: 'none' })}
              @click=${() => {
                const groupIndex = this._openGroups.findIndex(openGroup => openGroup === group.key);
                if (groupIndex === -1) {
                  // open this group
                  this._openGroups.push(group.key);
                } else {
                  // close this group
                  this._openGroups.splice(groupIndex, 1);
                }
                this.requestUpdate();
              }}
            >
              <d-text tiny>
                <d-icon> ${isOpen ? 'arrow-up' : 'arrow-down'} </d-icon>
                ${groupConfigs.length} Dienste
              </d-text>
            </d-flex>
          </d-flex>
        </d-flex>
        ${isOpen
          ? html`
              <d-flex ${css({ marginLeft: '24px' })} space="sm">
                ${repeat(
                  groupConfigs,
                  config => config.key,
                  config => this._renderConfig(config, state),
                )}
              </d-flex>
            `
          : nothing}
      </d-flex>
    `;
  }

  private _renderConfig(config: IConsentConfig, state: ConsentState) {
    if (config.isMandatory) state[config.key] = true;

    return html`
      <d-flex horizontal crossaxis-center space="sm" ${css({ position: 'relative' })}>
        ${config.isMandatory
          ? html`
              <div
                ${css({
                  position: 'absolute',
                  left: '0',
                  right: '0',
                  top: '0',
                  bottom: '0',
                  cursor: 'not-allowed',
                  zIndex: '99',
                })}
              ></div>
            `
          : nothing}
        <d-checkbox
          .value=${config.isMandatory ? true : state[config.key]}
          @value-picked=${(e: ValueEvent<boolean>) => {
            if (config.isMandatory) return;
            state[config.key] = e.detail.value;
            this.requestUpdate();
          }}
        ></d-checkbox>
        <d-flex space="xxs">
          <d-text> ${config.title} </d-text>
          <d-flex horizontal crossaxis-center space="xs" ${css({ color: '#7c7c7c' })}>
            <d-text tiny> ${config.description} </d-text>
          </d-flex>
        </d-flex>
      </d-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {},
      },
    ] as Styles[];
  }
}

@customElement('consent-initial-dialog')
export class ConsentInitialDialog extends DubniumDialog<ConsentState> {
  override connectedCallback(): void {
    super.connectedCallback();

    consentCookieValueSubject.subscribe(value => {
      const isInitialized = CookieFactory.consentCookie.getValue('init_consent', 'boolean');
      if (isInitialized) this.cancel();
    });
  }

  @property({ attribute: false }) groups = new RXController<Array<IConsentGroup | IConsentConfig>>(this, undefined);
  @state() private _showDetailed: boolean = false;

  override content() {
    const groups = this.groups.value;
    if (!groups || !groups.length) return html` <x-text> Configs not found </x-text> `;
    const state = convertGroupsToState(groups);

    return html`
      <d-flex space="lg">
        <d-text>
          Wir setzen auf unseren Websites Cookies und andere Technologien ein. Einige von ihnen sind essenziell (z.B.
          für die Sicherheit der Websites) andere sind optional und helfen uns dabei, die Nutzung unserer Websites für
          Dich zu verbessern. Details zu verwendeten Cookies und Technologien findest Du in unserer
          <d-link href="https://digitale-helden.de/startseite/datenschutz/" color="#fff"> Datenschutzerklärung </d-link
          >.
        </d-text>
        ${this._showDetailed
          ? html` <consent-item-groups .groups=${groups} .state=${state}></consent-item-groups> `
          : nothing}

        <d-flex horizontal crossaxis-center end space="sm" wrap>
          <d-button
            tone="black"
            small-padding
            @click=${() => {
              if (this._showDetailed) this.close(state);
              else this._showDetailed = true;
            }}
          >
            ${this._showDetailed ? 'Auswahl speichern' : 'Personalisieren'}
          </d-button>
          <d-button
            small-padding
            @click=${() => {
              for (const key of Object.keys(state)) {
                state[key] = true;
              }
              this.close(state);
            }}
          >
            Alle akzeptieren
          </d-button>
        </d-flex>
      </d-flex>
    `;
  }

  static override get element_name() {
    return 'consent-initial-dialog';
  }

  override styles() {
    return [
      ...super.styles(),
      {
        dialog: {
          boxSizing: 'border-box',
          userSelect: 'none',
          width: 'min(500px, 95%)',
          padding: '32px',
          borderRadius: '4px',
          background: '#333',
          color: '#fff',
          boxShadow: '0 4px 6px 0 rgba(0,0,0,0.2),5px 5px 10px 0 rgba(0,0,0,0.19)',
        },
        'dialog::backdrop': {
          backgroundColor: 'rgba(0, 0, 0, 0.6) !important',
        },
      },
    ] as Styles[];
  }
}
